/**
 * React component for displaying debug coordinate information.
 * Shows tile coordinates when debug mode is enabled.
 */

import { useEffect, useState } from 'react';

interface DebugCoordinateDisplayProps {
  scene: unknown | null;
}

export default function DebugCoordinateDisplay({ scene }: DebugCoordinateDisplayProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [coordinates, setCoordinates] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!scene || typeof scene !== 'object' || scene === null) return;

    const sceneWithMethods = scene as {
      getDebugOverlayController?: () => {
        getState?: () => { showCoordinates: boolean };
        toggleCoordinates?: () => void;
        getCurrentTileCoordinates?: () => { x: number; y: number } | null;
      } | null;
    };

    const debugController = sceneWithMethods.getDebugOverlayController?.();

    // Update coordinates and visibility state
    // Phaser handles the 'C' key toggle, we just sync the state here
    const updateCoordinates = () => {
      // Always update visibility state from controller
      const state = debugController?.getState?.();
      const shouldBeVisible = state?.showCoordinates ?? false;
      setIsVisible(shouldBeVisible);

      if (!shouldBeVisible) {
        setCoordinates(null);
        return;
      }

      // Get tile coordinates from debug controller
      const tileCoords = debugController?.getCurrentTileCoordinates?.();
      if (tileCoords) {
        setCoordinates(tileCoords);
      } else {
        setCoordinates(null);
      }
    };

    // Update frequently to catch state changes from Phaser keyboard handler
    const interval = setInterval(updateCoordinates, 50);

    return () => {
      clearInterval(interval);
    };
  }, [scene]);

  // Get initial state
  useEffect(() => {
    if (!scene || typeof scene !== 'object' || scene === null) return;

    const sceneWithMethods = scene as {
      getDebugOverlayController?: () => {
        getState?: () => { showCoordinates: boolean };
      } | null;
    };

    const debugController = sceneWithMethods.getDebugOverlayController?.();
    const state = debugController?.getState?.();
    setIsVisible(state?.showCoordinates ?? false);
  }, [scene]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed top-40 left-4 bg-gray-800 text-white p-3 rounded-lg shadow-xl border-2 border-gray-500 z-50 min-w-[200px]">
      <div className="text-sm font-bold mb-2 text-yellow-400">Debug Menu</div>
      <div className="text-xs mb-1 text-gray-400">Press C to toggle</div>
      <div className="text-xs font-mono mt-2">
        {coordinates ? (
          <div className="text-green-400">
            Tile: ({coordinates.x}, {coordinates.y})
          </div>
        ) : (
          <div className="text-gray-500">Move mouse to see coordinates</div>
        )}
      </div>
    </div>
  );
}

